import { useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { rv } from "../tokens";
import { XIcon } from "./icons";

const useStyles = makeStyles({
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1400,
    background: "rgba(15,23,20,.82)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    animation: "$rvFade .15s ease",
  },
  "@keyframes rvFade": { from: { opacity: 0 }, to: { opacity: 1 } },
  bar: {
    width: "min(900px, 92vw)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginRight: 12,
  },
  close: {
    border: "none",
    background: "rgba(255,255,255,.14)",
    color: "#fff",
    borderRadius: 8,
    width: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },
  imageWrap: {
    maxWidth: "min(900px, 92vw)",
    maxHeight: "80vh",
    overflow: "auto",
    borderRadius: 12,
    background: "#fff",
    boxShadow: rv.shadowPanel,
  },
  image: { display: "block", maxWidth: "100%", height: "auto" },
});

interface Props {
  open: boolean;
  src: string | null;
  title?: string;
  onClose: () => void;
}

// Full-screen preview for an on-file KYC document image. Closes on backdrop
// click, the close button, or Escape.
export default function ImageLightbox({ open, src, title, onClose }: Props) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const classes = useStyles();
  if (!open || !src) return null;

  const label = title || "Document preview";

  return (
    <div
      className={classes.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onClick={onClose}
    >
      <div className={classes.bar} onClick={(event) => event.stopPropagation()}>
        <span className={classes.title}>{label}</span>
        <button
          type="button"
          className={classes.close}
          onClick={onClose}
          aria-label="Close preview"
        >
          <XIcon size={18} />
        </button>
      </div>
      <div className={classes.imageWrap} onClick={(event) => event.stopPropagation()}>
        <img className={classes.image} src={src} alt={label} />
      </div>
    </div>
  );
}
